import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";

import { mockToken } from "../mocks/mockData";

import { loginRequest, msalConfig, tokenRequest } from "./msalConfig";

const DEFAULT_TOKEN_SCOPES = tokenRequest.scopes;
const DEFAULT_LOGIN_SCOPES = loginRequest.scopes;
const REDIRECT_STORAGE_KEY = "postLoginRedirect";

export const msalInstance = new PublicClientApplication(msalConfig);
let initializePromise = null;
let redirectPromise = null;
let interactiveLoginPromise = null;
const useAuthMock = process.env.REACT_APP_USE_MOCKS === "true";
const mockAccount = {
  username: "mock.user",
  name: "Mock User",
  localAccountId: "mock",
};

const ensureInitialized = async () => {
  if (!initializePromise) {
    initializePromise = msalInstance.initialize();
  }

  await initializePromise;
};

const setActiveAccountFromCache = () => {
  if (msalInstance.getActiveAccount()) {
    return;
  }

  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 1) {
    msalInstance.setActiveAccount(accounts[0]);
  }
};

const normalizeTokenScopes = () => DEFAULT_TOKEN_SCOPES;
const normalizeLoginScopes = () => DEFAULT_LOGIN_SCOPES;

const isOnRedirectCallback = () => {
  if (typeof window === "undefined") return false;

  return (
    window.location.pathname.includes("/Reports/auth/callback") ||
    window.location.pathname.includes("/auth/callback") ||
    window.location.search.includes("code=") ||
    window.location.hash.includes("code=")
  );
};

const normalizeRedirectPath = (path) => {
  if (!path || typeof path !== "string") return "/";

  // Never persist callback routes as post-login target.
  if (path.includes("/auth/callback")) return "/";

  const basePath = "/Reports";

  if (path.startsWith(`${basePath}/`)) {
    return path.slice(basePath.length) || "/";
  }

  if (path === basePath) {
    return "/";
  }

  return path;
};

const getPostLogoutRedirectUri = () => {
  if (msalConfig?.auth?.postLogoutRedirectUri) {
    return msalConfig.auth.postLogoutRedirectUri;
  }

  if (msalConfig?.auth?.redirectUri) {
    return msalConfig.auth.redirectUri;
  }

  if (typeof window === "undefined") return "/";

  return window.location.origin;
};

export const setPostLoginRedirect = (path) => {
  if (typeof window === "undefined") return;

  const defaultPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const redirectPath = normalizeRedirectPath(path || defaultPath);

  sessionStorage.setItem(REDIRECT_STORAGE_KEY, redirectPath);
};

export const consumePostLoginRedirect = () => {
  if (typeof window === "undefined") return null;

  const redirectPath = sessionStorage.getItem(REDIRECT_STORAGE_KEY);

  sessionStorage.removeItem(REDIRECT_STORAGE_KEY);

  return redirectPath;
};

export const initializeMsal = async () => {
  if (useAuthMock) {
    return msalInstance;
  }

  // eslint-disable-next-line no-console
  console.log("[msal] initialize");

  await ensureInitialized();
  await handleRedirectPromise();

  return msalInstance;
};

export const handleRedirectPromise = async () => {
  if (useAuthMock) {
    return { account: mockAccount };
  }

  if (!redirectPromise) {
    redirectPromise = (async () => {
      // eslint-disable-next-line no-console
      console.log("[msal] handleRedirectPromise");

      await ensureInitialized();

      const result = await msalInstance.handleRedirectPromise();

      // eslint-disable-next-line no-console
      console.log("[msal] handleRedirectPromise result", {
        hasAccount: Boolean(result?.account),
        hasIdToken: Boolean(result?.idToken),
      });

      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      } else {
        setActiveAccountFromCache();
      }

      return result;
    })();
  }

  return redirectPromise;
};

export const getActiveAccount = () =>
  useAuthMock
    ? mockAccount
    : (msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null);

export const acquireAccessToken = async (options = {}) => {
  if (useAuthMock) {
    return { accessToken: mockToken };
  }

  // eslint-disable-next-line no-console
  console.log("[msal] acquireAccessToken");

  await ensureInitialized();
  await handleRedirectPromise();

  const account = getActiveAccount();

  if (!account) {
    throw new InteractionRequiredAuthError("no_account", "No active account");
  }

  const requestOptions = { ...options };

  delete requestOptions.scopes;
  const tokenRequest = {
    ...requestOptions,
    scopes: normalizeTokenScopes(),
    account,
  };

  const result = await msalInstance.acquireTokenSilent(tokenRequest);

  // eslint-disable-next-line no-console
  console.log("[msal] acquireAccessToken result", {
    hasToken: Boolean(result?.accessToken),
    expiresOn: result?.expiresOn,
  });

  return result;
};

export const loginRedirect = (options = {}) => {
  const { redirectPath, ...requestOptions } = options;

  delete requestOptions.scopes;

  if (useAuthMock) {
    return Promise.resolve({ account: mockAccount });
  }

  if (isOnRedirectCallback()) {
    // eslint-disable-next-line no-console
    console.log("[msal] loginRedirect skipped on callback");

    return Promise.resolve();
  }

  if (interactiveLoginPromise) {
    // eslint-disable-next-line no-console
    console.log("[msal] loginRedirect skipped, interaction already in progress");

    return interactiveLoginPromise;
  }

  setPostLoginRedirect(redirectPath);

  const loginRequestPayload = {
    ...requestOptions,
    scopes: normalizeLoginScopes(),
  };

  // eslint-disable-next-line no-console
  console.log("[msal] loginRedirect", { scopes: loginRequestPayload.scopes });

  interactiveLoginPromise = msalInstance
    .loginRedirect(loginRequestPayload)
    .catch((error) => {
      if (error?.errorCode === "interaction_in_progress") {
        // eslint-disable-next-line no-console
        console.warn("[msal] interaction already in progress, skipping duplicate loginRedirect");

        return;
      }

      throw error;
    })
    .finally(() => {
      interactiveLoginPromise = null;
    });

  return interactiveLoginPromise;
};

export const logoutRedirect = (options = {}) => {
  if (useAuthMock) {
    return Promise.resolve();
  }

  const request = {
    postLogoutRedirectUri: getPostLogoutRedirectUri(),
    ...options,
  };

  // eslint-disable-next-line no-console
  console.log("[msal] logoutRedirect");

  return msalInstance.logoutRedirect(request);
};

export const isInteractionRequired = (error) =>
  error instanceof InteractionRequiredAuthError ||
  error?.errorCode === "interaction_required" ||
  error?.errorCode === "login_required" ||
  error?.errorCode === "consent_required";

export default msalInstance;
